import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Languages } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/#services", label: t("nav.services"), key: "services" },
    { to: "/#portfolio", label: t("nav.work"), key: "work" },
    { to: "/simulateur", label: t("nav.simulator"), key: "sim" },
    { to: "/#contact", label: t("nav.contact"), key: "contact" },
  ];

  const LangBtn = ({ extra = "" }) => (
    <button
      data-testid="lang-toggle"
      onClick={toggle}
      className={`group inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.15em] uppercase text-zinc-400 hover:text-white border border-white/15 px-2.5 py-1.5 transition-colors ${extra}`}
      aria-label="Toggle language"
    >
      <Languages size={12} strokeWidth={1.6} />
      {lang === "fr" ? "FR" : "EN"}
    </button>
  );

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 backdrop-blur-2xl bg-zinc-950/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <span className="block w-2.5 h-2.5 bg-white" />
          <span className="font-display text-xl tracking-tight">
            Versant<span className="text-zinc-500">.</span>Studio
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.to}
              data-testid={`navlink-${l.key}`}
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LangBtn />
          {user ? (
            <>
              {user.is_admin && (
                <button
                  data-testid="navbar-admin-btn"
                  onClick={() => navigate("/admin")}
                  className="text-sm text-emerald-300 hover:text-emerald-200 px-3 py-2"
                >
                  Admin
                </button>
              )}
              <button
                data-testid="navbar-dashboard-btn"
                onClick={() => navigate("/dashboard")}
                className="text-sm text-zinc-300 hover:text-white px-3 py-2"
              >
                {t("common.dashboard")}
              </button>
              <button
                data-testid="navbar-logout-btn"
                onClick={async () => {
                  navigate("/", { replace: true });
                  await logout();
                }}
                className="text-sm bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-4 py-2"
              >
                {t("common.logout")}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="navbar-login-btn"
                className="text-sm text-zinc-300 hover:text-white px-3 py-2"
              >
                {t("common.signin")}
              </Link>
              <Link
                to="/register"
                data-testid="navbar-register-btn"
                className="text-sm bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-4 py-2"
              >
                {t("common.signup")}
              </Link>
            </>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LangBtn />
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-zinc-200"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-zinc-950/95 px-6 py-6 space-y-4">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-navlink-${l.key}`}
              className="block text-zinc-300 hover:text-white"
            >
              {l.label}
            </a>
          ))}
          <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
            {user ? (
              <>
                {user.is_admin && (
                  <button
                    data-testid="mobile-admin-btn"
                    onClick={() => {
                      setOpen(false);
                      navigate("/admin");
                    }}
                    className="text-left text-emerald-300"
                  >
                    Admin
                  </button>
                )}
                <button
                  data-testid="mobile-dashboard-btn"
                  onClick={() => {
                    setOpen(false);
                    navigate("/dashboard");
                  }}
                  className="text-left text-zinc-300"
                >
                  {t("common.dashboard")}
                </button>
                <button
                  data-testid="mobile-logout-btn"
                  onClick={async () => {
                    setOpen(false);
                    navigate("/", { replace: true });
                    await logout();
                  }}
                  className="bg-white text-zinc-950 px-4 py-2 text-left"
                >
                  {t("common.logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="mobile-login-btn"
                  onClick={() => setOpen(false)}
                  className="text-zinc-300"
                >
                  {t("common.signin")}
                </Link>
                <Link
                  to="/register"
                  data-testid="mobile-register-btn"
                  onClick={() => setOpen(false)}
                  className="bg-white text-zinc-950 px-4 py-2"
                >
                  {t("common.signup")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
